// ===========================================
// Event Tier Pricing — shared display + picker
// ===========================================
// Reused across the admin / parent / public event surfaces so tier pricing
// renders and behaves identically everywhere. Two exports:
//
//   - <TierPriceList />      read-only list of offered tiers (label · $price),
//                            with a "from $X" helper.
//   - <TierQuantityPicker /> per-tier quantity steppers + running total, used by
//                            the parent & public checkout forms (emits items[]).
//
// Pricing is always driven by the server-provided EventTicketTier[] — the client
// never invents prices.

import { useMemo } from 'react';
import { Box, Stack, Typography, IconButton, Chip } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import type { CheckoutWords, EventTicketTierSummary, TicketItemInput } from '../../api/events.api';
import { localDateFromKey } from '../../utils/dateUtils';
import { NEUTRAL_CHECKOUT_WORDS } from '../../utils/checkoutWords';
import { capToSeats } from '../../utils/soldOut';

export const MAX_TICKETS_PER_ORDER = 20;
export const MAX_PER_TIER = 10;

/** Format integer cents as a dollar string, e.g. 2500 → "$25.00". */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** The cheapest offered tier price in cents, or null when there are none. */
export function minTierPriceCents(tiers: EventTicketTierSummary[] | undefined): number | null {
  if (!tiers || tiers.length === 0) return null;
  return Math.min(...tiers.map((t) => t.priceCents));
}

/**
 * A short price summary for chips/headers. "from $X" when more than one tier,
 * just "$X" for a single tier, null when none.
 */
export function tierPriceSummary(tiers: EventTicketTierSummary[] | undefined): string | null {
  if (!tiers || tiers.length === 0) return null;
  const min = minTierPriceCents(tiers)!;
  return tiers.length > 1 ? `from ${formatCents(min)}` : formatCents(min);
}

// ===========================================
// Read-only tier list
// ===========================================

interface TierPriceListProps {
  tiers: EventTicketTierSummary[];
  /** Visual density for the row text. */
  dense?: boolean;
}

/**
 * A tier's expiry as `(Exp dd/mm/yyyy)`, or '' when it never expires.
 *
 * ⚠️ `availableUntil` is a `@db.Date` — it arrives as a bare `YYYY-MM-DD` and reads
 * back at UTC midnight. It MUST be rebuilt at LOCAL midnight with
 * `localDateFromKey` before formatting: a plain `new Date()` renders the PREVIOUS
 * day west of UTC, which is the defect this repo has shipped three times.
 * en-AU dd/mm/yyyy per Critical Rule 7 — never US order, never a bare ISO string.
 */
function expirySuffix(availableUntil: string | null | undefined): string {
  if (!availableUntil) return '';
  const day = localDateFromKey(availableUntil).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return ` (Exp ${day})`;
}

/**
 * Renders each offered tier as "Label · $price", ordered as supplied (sortOrder).
 *
 * A tier with an end date shows it inline: `Early Bird (Exp 31/10/2026)`.
 * ⚠️ Only the ADMIN event-details page uses this component — buyer surfaces go
 * through `TicketPurchase` / `TicketCheckoutForm`. That matters: buyer reads
 * FILTER expired tiers out entirely, so a date would never be informative there,
 * whereas admin reads deliberately keep lapsed tiers visible so they stay editable.
 */
export function TierPriceList({ tiers, dense }: TierPriceListProps) {
  if (tiers.length === 0) return null;
  return (
    <Stack spacing={dense ? 0.25 : 0.5}>
      {tiers.map((tier) => (
        <Box
          key={tier.id}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {tier.label}
            {expirySuffix(tier.availableUntil)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCents(tier.priceCents)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** A compact "from $X" / "$X" chip for list cards and headers. */
export function TierPriceChip({ tiers }: { tiers: EventTicketTierSummary[] | undefined }) {
  const summary = tierPriceSummary(tiers);
  if (!summary) return null;
  return <Chip label={summary} size="small" variant="outlined" color="primary" />;
}

// ===========================================
// Per-tier quantity picker
// ===========================================

interface TierQuantityPickerProps {
  tiers: EventTicketTierSummary[];
  /** Map of tierId → quantity. */
  quantities: Record<string, number>;
  onChange: (quantities: Record<string, number>) => void;
  /** The server-resolved words for the event (aria labels). Omitted = "ticket". */
  words?: CheckoutWords;
  /** Ticket seats left (B3b, #687): the order cannot pass it. Omitted or null = no limit beyond the order cap. */
  seatsRemaining?: number | null;
}

/**
 * Renders one stepper row per offered tier (label, unit price, −/+), enforcing
 * a per-tier cap and a whole-order cap. Pure/controlled — the parent owns state.
 */
export function TierQuantityPicker({ tiers, quantities, onChange, words = NEUTRAL_CHECKOUT_WORDS, seatsRemaining }: TierQuantityPickerProps) {
  const orderCap = capToSeats(MAX_TICKETS_PER_ORDER, seatsRemaining);
  const totalQty = useMemo(
    () => Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0),
    [quantities]
  );

  const setQty = (tierId: string, next: number) => {
    const clamped = Math.max(0, Math.min(MAX_PER_TIER, next));
    // Never let the running total exceed the order cap.
    const others = totalQty - (quantities[tierId] || 0);
    const capped = Math.min(clamped, orderCap - others);
    onChange({ ...quantities, [tierId]: Math.max(0, capped) });
  };

  return (
    <Stack spacing={1.5}>
      {tiers.map((tier) => {
        const qty = quantities[tier.id] || 0;
        return (
          <Box
            key={tier.id}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {tier.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCents(tier.priceCents)} each
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label={`Remove one ${tier.label} ${words.singular}`}
                disabled={qty <= 0}
                onClick={() => setQty(tier.id, qty - 1)}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                {qty}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Add one ${tier.label} ${words.singular}`}
                disabled={qty >= MAX_PER_TIER || totalQty >= orderCap}
                onClick={() => setQty(tier.id, qty + 1)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

/** Total cents for a tierId→qty map against the given tiers. */
export function tierOrderTotalCents(
  tiers: EventTicketTierSummary[],
  quantities: Record<string, number>
): number {
  return tiers.reduce((sum, t) => sum + t.priceCents * (quantities[t.id] || 0), 0);
}

/** Convert a tierId→qty map into the items[] checkout payload (drops zero lines). */
export function quantitiesToItems(quantities: Record<string, number>): TicketItemInput[] {
  return Object.entries(quantities)
    .filter(([, qty]) => qty > 0)
    .map(([tierId, quantity]) => ({ tierId, quantity }));
}
